import { Record, List } from 'immutable';
import _ from 'lodash';

import Organization from '../organization/model';
import Deed from '../deed/model';

const properties = {
  id: '',
  name: '',
  title: '',
  tagline: '',
  description: '',
  cta: '',
  bannerSquare: '',
  bannerRectangle: '',
  colorA: '',
  colorB: '',
  sorting: 0,
  ctaButtonColor: '',
  ctaTextColor: '',
  customFont: '',
  linkOnly: false,
  organizations: [],
  public: false,
  shareLink: '',
  contentLocationFiltering: false,
  totalHeadline: '',
  totalText: '',
  nameColor: '',
  backgroundHeroColor: '',
  backgroundDeedsColor: '',
  createdAt: '',
  startingAt: '',
  endingAt: '',
  pledgedDonationStartingAt: '',
  pledgedDonationEndingAt: '',
  allowOneTimePledge: undefined,
  company: undefined,
  backgroundImage: '',
  nonprofits: null,
  deeds: null,
  fundraisers: null,
  volunteering: null,
  events: null,
  locationsExclude: true,
  isPledge: false,
  locations: List(),
  contentSorting: {
    items: {
      fundraisers: [],
      volunteering: [],
      events: [],
      nonprofits: [],
    },
    sections: [],
  },
  contentSortingSections: List(['Fundraisers', 'Nonprofits', 'Volunteering', 'Events']),
  goalAmount: undefined,
  goalCurrencyCode: undefined,
  isSharedWithSubsidiary: false,
  optedInCompanies: [],
  optedIn: false,
  companySubsidiaryAccess: false,
  hideProgressBar: false,
  hideActivityFeed: false,
  showLinkToDonateSearch: false,
  autoTagDonations: false,
  videoLink: '',
  editors: [],
};

interface ContentSorting {
  items: {
    fundraisers: string[];
    volunteering: string[];
    events: string[];
    nonprofits: string[];
    actions: string[];
  };
  sections: string[];
}

const mapDeedToContentType = (deedType: string): string => {
  switch (deedType) {
    case 'Campaign':
      return 'fundraisers';

    case 'Project':
    case 'Event':
      return 'volunteering';

    case 'BaseEvent':
      return 'events';

    default:
      throw new Error('unknown Deed type');
  }
};

const sortContent = (contents, contentSorting, contentType: string) => {
  const contentSortingByType = contentSorting.items[mapDeedToContentType(contentType)];

  const contentWithSorting: Deed[] = contentSortingByType
    ? contentSortingByType
        .map((eachContentSorting) =>
          contents.find((eachDeed) => eachDeed.get('id') === eachContentSorting)
        )
        .filter((eachContent) => eachContent)
    : [];

  const contentWithoutSorting: Deed[] = contents.filter(
    (eachDeed) => !contentSortingByType.includes(eachDeed.id)
  );

  const contentSorted = contentWithSorting.concat(contentWithoutSorting);

  return contentSorted;
};

export default class Campaign extends Record(properties, 'Campaign') implements Campaign {
  public readonly id!: string;

  public readonly goalAmount?: number;

  public readonly goalCurrencyCode?: string;

  public readonly name!: string;

  public readonly title!: string;

  public readonly description!: string;

  public readonly tagline!: string;

  public readonly cta!: string;

  public readonly bannerSquare!: string;

  public readonly bannerRectangle!: string;

  public readonly colorA!: string;

  public readonly colorB!: string;

  public readonly sorting!: number;

  public readonly ctaButtonColor!: string;

  public readonly ctaTextColor!: string;

  public readonly customFont!: string;

  public readonly linkOnly!: boolean;

  public readonly organizations!: string[];

  public readonly editors!: string[];

  public readonly submitter?: string;

  public readonly public!: boolean;

  public readonly shareLink!: string;

  public readonly totalHeadline!: string;

  public readonly totalText!: string;

  public readonly nameColor!: string;

  public readonly backgroundHeroColor!: string;

  public readonly backgroundDeedsColor!: string;

  public readonly createdAt!: Date;

  public readonly startingAt!: Date;

  public readonly endingAt!: Date;

  public readonly pledgedDonationStartingAt!: Date;

  public readonly pledgedDonationEndingAt!: Date;

  public readonly allowOneTimePledge?: boolean;

  public readonly isPledge!: boolean;

  public readonly company!: Organization;

  public readonly backgroundImage!: string;

  public readonly nonprofits!: List<Organization>;

  public readonly deeds!: List<Deed>;

  public readonly fundraisers!: List<Deed>;

  public readonly volunteering!: List<Deed>;

  public readonly events!: List<Deed>;

  public readonly locationExclude!: boolean;

  public readonly locations!: List<any>;

  public readonly contentSorting!: ContentSorting;

  public readonly contentSortingSections!: List<string>;

  public readonly isSharedWithSubsidiary!: boolean;

  public readonly optedInCompanies!: [];

  public readonly optedIn!: boolean;

  public readonly companySubsidiaryAccess!: boolean;

  public readonly hideProgressBar!: boolean;

  public readonly hideActivityFeed!: boolean;

  public readonly autoTagDonations!: boolean;

  public readonly showLinkToDonateSearch!: boolean;

  public readonly videoLink!: string;

  constructor(values: any = {}, isActionsFeatureEnabled = false) {
    const validValues = _.pick(values, Object.keys(properties));
    super({
      ...validValues,
      createdAt: values.createdAt ? new Date(values.createdAt) : undefined,
      startingAt: values.startingAt ? new Date(values.startingAt) : undefined,
      endingAt: values.endingAt ? new Date(values.endingAt) : undefined,
      pledgedDonationStartingAt: values.pledgedDonationStartingAt
        ? new Date(values.pledgedDonationStartingAt)
        : undefined,
      pledgedDonationEndingAt: values.pledgedDonationEndingAt
        ? new Date(values.pledgedDonationEndingAt)
        : undefined,

      company: values.company ? new Organization(values.company) : undefined,
      nonprofits:
        values.nonprofits &&
        List(values.nonprofits.map((nonprofit: Organization) => new Organization(nonprofit))),
      fundraisers:
        values.deeds &&
        List(
          sortContent(
            values.deeds
              .filter((eachDeed) => eachDeed.__t === 'Campaign')
              .map((deed: Deed) => new Deed(deed)),
            validValues.contentSorting,
            'Campaign'
          )
        ),
      volunteering:
        values.deeds &&
        List(
          sortContent(
            values.deeds
              .filter((eachDeed) => ['Project', 'Event'].includes(eachDeed.__t))
              .map((deed: Deed) => new Deed(deed)),
            validValues.contentSorting,
            'Project'
          )
        ),
      events:
        values.deeds &&
        List(
          sortContent(
            values.deeds
              .filter((eachDeed) => eachDeed.__t === 'BaseEvent')
              .map((deed: Deed) => new Deed(deed)),
            validValues.contentSorting,
            'BaseEvent'
          )
        ),
      locations: List(values.locations || []),
      contentSortingSections:
        (values.contentSortingSections && List(values.contentSortingSections)) ||
        (Array.isArray(values.contentSorting?.sections) &&
          values.contentSorting.sections.length &&
          List(values.contentSorting?.sections)) ||
        List([
          'Fundraisers',
          'Nonprofits',
          'Volunteering',
          'Events',
          ...(isActionsFeatureEnabled ? ['Actions'] : []),
        ]),
    });
  }

  public get isActive(): boolean {
    const now = new Date().getTime();
    if (this.startingAt) {
      if (this.endingAt) {
        return this.startingAt.getTime() <= now && this.endingAt.getTime() >= now;
      } else {
        return this.startingAt.getTime() <= now;
      }
    }
    return false;
  }

  public get privacy() {
    return this.companySubsidiaryAccess ? 'shared' : this.public ? 'public' : 'company';
  }

  public toForm() {
    return { ...this.toJS(), privacy: this.privacy };
  }
}
